import { formatRelativeTime } from '@/utils/format-time';

export interface InlineCommentData {
  /** Unique comment ID */
  id: string;
  /** File path the comment is on */
  filePath: string;
  /** Line number the comment is anchored to */
  lineNumber: number;
  /** Side of the diff (LEFT for removed lines, RIGHT for added lines) */
  side: 'LEFT' | 'RIGHT';
  /** Comment body text */
  body: string;
  /** Author information */
  author: { displayName: string };
  /** ISO 8601 timestamp of when the comment was created */
  createdAt: string;
  /** Whether the comment is outdated (file/line changed since comment was made) */
  outdated: boolean;
  /** Parent comment ID for threaded replies */
  parentId?: string;
  /** Whether the thread is resolved */
  resolved: boolean;
  /** Whether this is an AI review comment */
  isAiReview?: boolean;
  /** Whether the AI comment has been dismissed */
  dismissed?: boolean;
}

export interface InlineCommentProps {
  /** The comment data to display */
  comment: InlineCommentData;
  /** Optional current time for relative time formatting (for testing) */
  now?: Date;
}

/**
 * Displays an existing inline comment at its file/line position.
 * Shows author, relative time, and comment body.
 * Outdated comments have muted styling and an "outdated" label.
 */
export function InlineComment({ comment, now }: InlineCommentProps) {
  const isOutdated = comment.outdated;

  return (
    <div
      className={`border-t border-b px-4 py-3 ${
        isOutdated
          ? 'border-border bg-surface-raised opacity-70'
          : 'border-accent-200 bg-accent-50 dark:border-accent-800 dark:bg-accent-950/20'
      }`}
      data-testid={`inline-comment-${comment.id}`}
      aria-label={`Comment by ${comment.author.displayName}${isOutdated ? ' (outdated)' : ''}`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`text-sm font-medium ${
            isOutdated
              ? 'text-content-tertiary'
              : 'text-content'
          }`}
        >
          {comment.author.displayName}
        </span>

        <span
          className={`text-xs ${
            isOutdated
              ? 'text-content-tertiary'
              : 'text-content-secondary'
          }`}
        >
          {formatRelativeTime(comment.createdAt, now)}
        </span>

        {isOutdated && (
          <span
            className="inline-flex rounded bg-warning/15 px-1.5 py-0.5 text-xs font-medium text-warning"
            data-testid="outdated-label"
          >
            outdated
          </span>
        )}
      </div>

      <p
        className={`mt-1 whitespace-pre-wrap text-sm ${
          isOutdated
            ? 'text-content-tertiary'
            : 'text-content-secondary'
        }`}
      >
        {comment.body}
      </p>

      {isOutdated && (
        <p className="mt-1 text-xs text-content-tertiary">
          {comment.filePath}:{comment.lineNumber} ({comment.side})
        </p>
      )}
    </div>
  );
}
